import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { usersAPI } from "../api";
import { toast } from "react-toastify";
import { useT } from "../../../i18n";

// Get all partners hook
export const useGetAllPartners = () => {
  return useQuery({
    queryKey: ["partners"],
    queryFn: usersAPI.getPartners,
    retry: 2,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

// Get all users hook
export const useGetAllUsers = () => {
  return useQuery({
    queryKey: ["users"],
    queryFn: usersAPI.getAllUsers,
    retry: 2,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

// Create partner hook
export const useCreatePartner = () => {
  const queryClient = useQueryClient();
  const t = useT();

  return useMutation({
    mutationFn: (data: {
      companyName: string;
      companyVatId: string;
      partnershipLevel?: string;
    }) => usersAPI.createPartner(data),
    onSuccess: (data: any) => {
      toast.success(t("users.toast.partnerCreated"));
      queryClient.invalidateQueries({ queryKey: ["partners"] });
    },
    onError: (error: any) => {
      toast.error(t("users.toast.partnerCreateFailed"));
    },
  });
};

// Register user hook
export const useRegisterUser = () => {
  const queryClient = useQueryClient();
  const t = useT();

  return useMutation({
    mutationFn: usersAPI.registerUser,
    onSuccess: () => {
      toast.success(t("users.toast.registered"));
      queryClient.invalidateQueries({ queryKey: ["users"] });
    },
    onError: (error: any) => {
      toast.error(t("users.toast.registerFailed"));
    },
  });
};

// Update user hook
export const useUpdateUser = () => {
  const queryClient = useQueryClient();
  const t = useT();

  return useMutation({
    mutationFn: ({ uuid, data }: { uuid: string; data: any }) =>
      usersAPI.updateUser(uuid, data),
    onSuccess: () => {
      toast.success(t("users.toast.updated"));
      queryClient.invalidateQueries({ queryKey: ["users"] });
    },
    onError: (error: any) => {
      toast.error(t("users.toast.updateFailed"));
    },
  });
};

// Delete user hook
export const useDeleteUser = () => {
  const queryClient = useQueryClient();
  const t = useT();

  return useMutation({
    mutationFn: usersAPI.deleteUser,
    onSuccess: () => {
      toast.success(t("users.toast.deleted"));
      queryClient.invalidateQueries({ queryKey: ["users"] });
    },
    onError: (error: any) => {
      toast.error(t("users.toast.deleteFailed"));
    },
  });
};
